import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { getPerformanceReview } from "../pages/services/performanceService";
import { DIFFICULTY_ORDER, difficultyColor } from "./utils/difficulty";
import "../styles/PerformanceReview.css";

// Same colours as the Trainer screen, so the two read alike.
const COLORS = {
  attempted: "#2563EB",
  correct: "#059669",
  wrong: "#EF4444",
  topics: "#0D9488",
  questions: "#2563EB",
};

// Difficulty colours come from the shared map so this chart matches the
// question chips and the Test Score Summary: green -> orange -> red -> maroon.
const DIFFICULTY_RAMP = DIFFICULTY_ORDER.map((level) => ({ level, color: difficultyColor(level) }));

const shortDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

const Tile = ({ label, value, sub }) => (
  <div className="pr-tile">
    <div className="pr-tile-label">{label}</div>
    <div className="pr-tile-value">{value}</div>
    {sub && <div className="pr-tile-sub">{sub}</div>}
  </div>
);

const Card = ({ title, note, sub, children }) => (
  <div className="pr-card">
    <div className="pr-card-head">
      <h3 className="pr-card-title">{title}</h3>
      {note && <span className="pr-card-note">{note}</span>}
    </div>
    {sub && <p className="pr-card-sub">{sub}</p>}
    {children}
  </div>
);

const Empty = ({ children }) => <div className="pr-empty">{children}</div>;

// Difficulty accuracy arrives as { date, Easy: {accuracy,…}, Hard: {…} }.
// recharts needs flat keys, and a level absent on a day must stay a gap rather
// than plotting as zero accuracy.
const flattenDifficulty = (series) =>
  series.map((row) => {
    const out = { date: row.date };
    Object.entries(row).forEach(([k, v]) => {
      if (k !== "date" && v && typeof v === "object") out[k] = v.accuracy;
    });
    return out;
  });

const TopicList = ({ topics, kind }) => {
  if (!topics.length) return <Empty>No topics in this band yet.</Empty>;
  const bySubject = topics.reduce((acc, t) => {
    (acc[t.subjectTitle || "Unassigned"] = acc[t.subjectTitle || "Unassigned"] || []).push(t);
    return acc;
  }, {});
  return (
    <div className="pr-topic-groups">
      {Object.entries(bySubject).map(([subject, list]) => (
        <div className="pr-topic-group" key={subject}>
          <p className="pr-topic-subject">{subject}</p>
          {list.map((t) => (
            <div className="pr-topic-row" key={t.topicId}>
              <span className="pr-topic-title">{t.topicTitle}</span>
              <span className={`pr-topic-acc pr-${kind}`}>{t.accuracy}%</span>
              <span className="pr-topic-meta">{t.correct}/{t.attempted}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const ConsistencyList = ({ rows }) => {
  if (!rows.length) return <Empty>No students in scope.</Empty>;
  return (
    <div className="pr-consistency">
      {rows.map((c) => (
        <div className="pr-con-row" key={c.studentId}>
          <div className="pr-con-head">
            <span className="pr-con-name">{c.studentName}</span>
            <span className="pr-con-score">{c.score}<span className="pr-con-ceiling">/{c.ceiling}</span></span>
          </div>
          <div className="pr-con-days" title={`${c.activeDays} of ${c.windowDays} days met the ${c.dailyBar}-question bar`}>
            {c.days.map((d) => (
              <span key={d.date} className={`pr-con-day${d.met ? " pr-con-day--met" : ""}`} title={`${d.date}: ${d.count} question(s)`} />
            ))}
          </div>
          <p className="pr-con-meta">
            {c.activeDays}/{c.windowDays} active days · topics {c.topicsCompleted}/{c.topicsTotal}
            {!c.allTopicsCovered && <span className="pr-con-cap"> · ceiling capped at 50 until every topic is opened</span>}
          </p>
        </div>
      ))}
    </div>
  );
};

const PerformanceReviewPanels = ({ filters = {} }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scope = JSON.stringify({
    courseId: filters.courseId || "", batchId: filters.batchId || "",
    studentId: filters.studentId || "", subjectId: filters.subjectId || "",
    from: filters.from || "", to: filters.to || "",
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPerformanceReview(JSON.parse(scope))
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  if (loading) return <div className="pr-state">Loading performance review…</div>;
  if (error) return <div className="pr-state pr-state-error">{error}</div>;
  if (!data) return null;

  const t = data.totals;
  const strong = data.topicAccuracy.filter((x) => x.accuracy > data.thresholds.strong);
  const weak = data.topicAccuracy.filter((x) => x.accuracy < data.thresholds.weak);
  const difficultyRows = flattenDifficulty(data.difficultySeries);
  const levelsPresent = DIFFICULTY_RAMP.filter((d) => data.difficultyLevels.includes(d.level));

  if (!t.studentsInScope) {
    return <div className="pr-state">No students match this selection, so there is nothing to report.</div>;
  }

  return (
    <div className="pr-root">
      <div className="pr-tiles">
        <Tile label="Students" value={t.studentsInScope} />
        <Tile label="Questions attempted" value={t.attempted} />
        <Tile label="Accuracy" value={`${t.accuracy}%`} sub={`${t.correct} correct · ${t.wrong} wrong`} />
        <Tile label="Topics completed" value={t.topicsCompleted} sub={`of ${t.topicsTotal} in scope`} />
      </div>

      <Card title="Practice" sub="Questions answered per day, split by outcome.">
        {data.practiceSeries.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.practiceSeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis allowDecimals={false} stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip labelFormatter={shortDate} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="attempted" name="Attempted" stroke={COLORS.attempted} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="correct" name="Correct" stroke={COLORS.correct} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="wrong" name="Wrong" stroke={COLORS.wrong} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty>No questions answered in this range.</Empty>}
      </Card>

      <Card title="Study" sub="Topics completed alongside questions attempted, per day.">
        {data.studySeries.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.studySeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis allowDecimals={false} stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip labelFormatter={shortDate} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="topicsCompleted" name="Topics completed" stroke={COLORS.topics} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="questionsAttempted" name="Questions attempted" stroke={COLORS.questions} strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty>No study activity in this range.</Empty>}
      </Card>

      <Card
        title="Consistency"
        note={`last ${data.consistency[0]?.windowDays ?? 7} days`}
        sub={`Full marks needs ${data.consistency[0]?.dailyBar ?? 5}+ questions every day with every topic covered; the ceiling halves while topics remain unopened, and each missed day costs a seventh.`}
      >
        <ConsistencyList rows={data.consistency} />
      </Card>

      <div className="pr-two-col">
        <Card title="Strength topics" note={`above ${data.thresholds.strong}%`} sub="Grouped by subject.">
          <TopicList topics={strong} kind="strong" />
        </Card>
        <Card title="Weak topics" note={`below ${data.thresholds.weak}%`} sub="Grouped by subject.">
          <TopicList topics={weak} kind="weak" />
        </Card>
      </div>

      <Card title="Difficulty-wise accuracy" sub="Accuracy per day for each difficulty level. Days with no questions at a level are skipped.">
        {difficultyRows.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={difficultyRows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis domain={[0, 100]} unit="%" stroke="#cbd5e1" tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip labelFormatter={shortDate} formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {levelsPresent.map((d) => (
                <Line key={d.level} type="monotone" dataKey={d.level} name={d.level}
                  stroke={d.color} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty>No questions answered in this range.</Empty>}
      </Card>
    </div>
  );
};

export default PerformanceReviewPanels;
